import { useEffect, useMemo, ReactNode } from 'react'
import { create } from 'zustand'
import { useGetAppPage } from './useGetAppPage'
import { PageUiSchema, TComponentType, TContainer, TPageFilter, TPosition } from './types'
import { toast } from 'sonner'
import { buildPage } from './utils'
import { useAppPageState } from './useAppPageState'
import { useIsPageBuilder } from '@/catalog-apps/hooks/useIsPageBuilder'
import { useLocation } from 'react-router'

export const DEFAULT_PAGE_LAYOUT = {
  id: '/page-builder-page',
  title: 'New Page Name',
  description: 'New Page Description',
  gridLayout: { cols: 2, rows: 2 },
  roles: [] as string[],
  pageFilters: [] as TPageFilter[],
  containers: [
    {
      id: '1',
      position: {
        colStart: 1,
        rowStart: 1,
        colSpan: 1,
        rowSpan: 1,
      },
      component: {
        type: 'component-type' as TComponentType,
        id: '1-your-component-id',
      },
    },
    {
      id: '2',
      position: {
        colStart: 2,
        rowStart: 1,
        colSpan: 1,
        rowSpan: 1,
      },
      component: {
        type: 'component-type' as TComponentType,
        id: '2-your-component-id',
      },
    },
  ],
}

// LocalStorage helpers to preserve existing key and shape used previously
const SAVED_KEY = 'savedPageLayouts'
const readSavedLayouts = (): PageUiSchema[] => {
  try {
    const raw = localStorage.getItem(SAVED_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as PageUiSchema[]) : []
  } catch {
    return []
  }
}
const writeSavedLayouts = (layouts: PageUiSchema[]) => {
  try {
    localStorage.setItem(SAVED_KEY, JSON.stringify(layouts))
    // Dispatch a synthetic storage event so components using the custom
    // useLocalStorage hook reactively update in the same tab
    window.dispatchEvent(
      new StorageEvent('storage', {
        key: SAVED_KEY,
        newValue: JSON.stringify(layouts),
        storageArea: localStorage,
      })
    )
  } catch {
    // ignore
  }
}

type SetTitleDescriptionAction =
  | { title?: string; description?: string }
  | ((prev: { title?: string; description?: string }) => { title?: string; description?: string })

interface PageLayoutStoreState {
  // base data from server (used to preserve roles, filters, etc.)
  baseUiSchema?: PageUiSchema

  // mutable UI state
  containers: TContainer[]
  titleDesrcription: { title?: string; description?: string }
  currentLayoutId?: string
  // explicit grid for builder mode
  gridCols: number
  gridRows: number

  // initialization
  initializeFromData: (ui: PageUiSchema) => void

  // public API actions (mirrors previous context)
  setTitleDesrcription: (action: SetTitleDescriptionAction) => void
  setDefaultContainers: () => void
  handleLayoutChange: (containerId: string, position: TPosition) => void
  handleResetPageLayout: () => void
  handleAddContainer: () => void
  setComponentType: (containerId: string, componentType: TComponentType) => void
  handleDeleteContainer: (containerId: string) => void
  copyJsonLayout: (type: 'json' | 'py') => void
  createNewPageLayout: () => void
  savePageLayout: () => void
  updateSavedPageLayout: (id: string) => void
  loadPageLayout: (id?: string) => void
  deletePageLayout: (id: string) => void
  setCurrentLayoutId: (id?: string) => void

  // position/size adjustments
  incrVerticalTop: (containerId: string) => void
  moveUp: (containerId: string) => void
  decrVerticalTop: (containerId: string) => void
  decrHorizontalRight: (containerId: string) => void
  moveRight: (containerId: string) => void
  incrHorizontalRight: (containerId: string) => void
  incrVerticalBottom: (containerId: string) => void
  moveDown: (containerId: string) => void
  decrVerticalBottom: (containerId: string) => void
  decrHorizontalLeft: (containerId: string) => void
  moveLeft: (containerId: string) => void
  incrHorizontalLeft: (containerId: string) => void

  // batch apply a 3rd-party layout (e.g., react-grid-layout)
  applyLayout: (layout: { i: string; x: number; y: number; w: number; h: number }[]) => void

  // grid controls
  addColumn: () => void
  removeColumn: () => void
  addRow: () => void
  removeRow: () => void
  setGridCols: (cols: number) => void
  setGridRows: (rows: number) => void
  trimGrid: () => void
}

const computeRows = (containers: TContainer[]): number => {
  if (!containers.length) return 0
  return Math.max(...containers.map((container) => container.position.rowSpan + container.position.rowStart - 1))
}
const computeCols = (containers: TContainer[]): number => {
  if (!containers.length) return 0
  return Math.max(...containers.map((container) => container.position.colSpan + container.position.colStart - 1))
}

export const usePageLayoutStore = create<PageLayoutStoreState>((set, get) => ({
  baseUiSchema: undefined,

  containers: [],
  titleDesrcription: { title: undefined, description: undefined },
  currentLayoutId: undefined,
  gridCols: 1,
  gridRows: 2,

  initializeFromData: (ui) => {
    const inferredCols = computeCols(ui.containers)
    const inferredRows = computeRows(ui.containers)
    set({
      baseUiSchema: ui,
      containers: ui.containers,
      titleDesrcription: { title: ui.title, description: ui.description },
      currentLayoutId: undefined,
      // Ignore incoming gridLayout for initial builder state and infer from containers.
      // Fall back to explicit defaults when containers are empty.
      gridCols: inferredCols || 2,
      gridRows: inferredRows || 1,
    })
  },

  setTitleDesrcription: (action) =>
    set((state) => {
      const prev = state.titleDesrcription
      const next = typeof action === 'function' ? (action as (p: typeof prev) => typeof prev)(prev) : action
      return { titleDesrcription: next }
    }),

  setDefaultContainers: () => {
    // set({ containers: DEFAULT_PAGE_LAYOUT.containers as TContainer[] })
    const base = get().baseUiSchema
    if (!base) return
    set({ containers: base.containers })
  },

  handleLayoutChange: (containerId, position) => {
    const updated = get().containers.map((container) =>
      container.id === containerId ? { ...container, position } : container
    )
    set({ containers: updated })
  },

  handleResetPageLayout: () => {
    const base = get().baseUiSchema
    if (!base) return
    set({ containers: base.containers })
    toast.success('Page Layout successfully reset to default')
  },

  handleAddContainer: () => {
    const { containers, gridCols, gridRows } = get()
    const numberOfContainers = containers.length
    const uid = Math.random().toString(36).substring(2, 9)

    // Build occupancy grid
    const occupied: boolean[][] = Array.from({ length: gridRows }, () => Array.from({ length: gridCols }, () => false))
    for (const c of containers) {
      const { colStart, rowStart, colSpan, rowSpan } = c.position
      for (let r = rowStart - 1; r < rowStart - 1 + rowSpan && r < gridRows; r++) {
        for (let col = colStart - 1; col < colStart - 1 + colSpan && col < gridCols; col++) {
          if (r >= 0 && col >= 0) occupied[r][col] = true
        }
      }
    }

    // Find first free cell row-major
    let targetRow = -1
    let targetCol = -1
    outer: for (let r = 0; r < gridRows; r++) {
      for (let c = 0; c < gridCols; c++) {
        if (!occupied[r][c]) {
          targetRow = r + 1
          targetCol = c + 1
          break outer
        }
      }
    }

    // If full, add a new row at the bottom
    let newGridRows = gridRows
    if (targetRow === -1 || targetCol === -1) {
      newGridRows = gridRows + 1
      targetRow = newGridRows
      targetCol = 1
    }

    const newContainer: TContainer = {
      id: `${numberOfContainers + 1}-${uid}`,
      position: { colStart: targetCol, rowStart: targetRow, colSpan: 1, rowSpan: 1 },
      component: { type: 'component-type' as TComponentType, id: `${numberOfContainers + 1}-your-component-id` },
    }

    set({ containers: [...containers, newContainer], gridRows: newGridRows })
  },

  setComponentType: (containerId, componentType) => {
    const updated = get().containers.map((container) =>
      container.id === containerId
        ? { ...container, component: { type: componentType, id: `${containerId}-your-component-id` } }
        : container
    )
    set({ containers: updated })
  },

  handleDeleteContainer: (containerId) => {
    const updated = get().containers.filter((container) => container.id !== containerId)
    set({ containers: updated })
  },

  copyJsonLayout: (type) => {
    const base = get().baseUiSchema
    const { containers, titleDesrcription, gridCols, gridRows } = get()
    const obj: PageUiSchema = {
      ...(base || {
        id: '',
        title: undefined,
        description: undefined,
        roles: [],
        pageFilters: [],
        containers: [],
      }),
      containers,
      title: titleDesrcription.title,
      description: titleDesrcription.description,
      id: 'your-page-id',
      gridLayout: { cols: gridCols, rows: gridRows },
    }
    const json = JSON.stringify({ uiSchema: obj }, null, 2)
    const py = buildPage(json)
    navigator.clipboard.writeText(type === 'json' ? json : py)
    toast.success('Page Layout successfully copied to clipboard')
  },

  createNewPageLayout: () => {
    const base = get().baseUiSchema
    if (!base) return
    set({
      titleDesrcription: { title: 'New Page Name', description: 'New Page Description' },
      containers: base.containers,
      currentLayoutId: undefined,
    })
  },

  savePageLayout: () => {
    const base = get().baseUiSchema
    const { containers, titleDesrcription, gridCols, gridRows } = get()
    const savedLayoutId = Math.random().toString(36).substring(2, 9)
    const obj: PageUiSchema = {
      ...(base || {
        id: savedLayoutId,
        title: undefined,
        description: undefined,
        roles: [],
        pageFilters: [],
        containers: [],
      }),
      id: savedLayoutId,
      containers,
      title: titleDesrcription.title,
      description: titleDesrcription.description,
      gridLayout: { cols: gridCols, rows: gridRows },
    }
    const list = readSavedLayouts()
    writeSavedLayouts([...list, obj])
    set({ currentLayoutId: savedLayoutId })
    toast.success('Page Layout successfully saved')
  },

  updateSavedPageLayout: (id) => {
    const { containers, titleDesrcription, gridCols, gridRows } = get()
    const list = readSavedLayouts()
    const updated = list.map((layout) =>
      layout.id === id
        ? {
            ...layout,
            containers,
            title: titleDesrcription.title,
            description: titleDesrcription.description,
            gridLayout: { cols: gridCols, rows: gridRows },
          }
        : layout
    )
    writeSavedLayouts(updated)
    toast.success('Page Layout successfully updated')
  },

  loadPageLayout: (id) => {
    const list = readSavedLayouts()
    const layout = list.find((l) => l.id === id)
    if (!layout) return
    const inferredCols = computeCols(layout.containers)
    const inferredRows = computeRows(layout.containers)
    set({
      titleDesrcription: { title: layout.title, description: layout.description },
      containers: layout.containers,
      currentLayoutId: id,
      gridCols: layout.gridLayout?.cols ?? inferredCols,
      gridRows: layout.gridLayout?.rows ?? inferredRows,
    })
  },

  deletePageLayout: (id) => {
    const base = get().baseUiSchema
    const list = readSavedLayouts()
    const updated = list.filter((l) => l.id !== id)
    writeSavedLayouts(updated)
    if (base) {
      set({
        titleDesrcription: { title: base.title, description: base.description },
        containers: base.containers,
        currentLayoutId: undefined,
        gridCols: base.gridLayout?.cols ?? computeCols(base.containers),
        gridRows: base.gridLayout?.rows ?? computeRows(base.containers),
      })
    } else {
      set({ currentLayoutId: undefined })
    }
  },

  setCurrentLayoutId: (id) => {
    set({ currentLayoutId: id })
  },

  // ----- position/size adjustments -----
  incrVerticalTop: (containerId) => {
    const containers = get().containers
    const container = containers.find((c) => c.id === containerId)
    if (!container) return
    const { position } = container
    const { rowStart, rowSpan } = position
    const newRowStart = rowStart - 1
    const newRowSpan = rowSpan + 1
    const newPosition = { ...position, rowStart: newRowStart, rowSpan: newRowSpan }
    if (rowStart === 1) {
      const otherContainers = containers.filter((c) => c.id !== containerId)
      const otherContainersNewPosition = otherContainers.map((c) => ({
        ...c,
        position: { ...c.position, rowStart: c.position.rowStart + 1 },
      }))
      set({
        containers: [
          { ...container, position: { ...position, rowStart: 1, rowSpan: newRowSpan } },
          ...otherContainersNewPosition,
        ],
      })
      return
    }
    get().handleLayoutChange(containerId, newPosition)
  },

  moveUp: (containerId) => {
    const containers = get().containers
    const container = containers.find((c) => c.id === containerId)
    const otherContainers = containers.filter((c) => c.id !== containerId)
    if (!container) return
    const { position } = container
    const { rowStart } = position
    if (rowStart === 1) {
      const otherContainersNewPosition = otherContainers.map((c) => ({
        ...c,
        position: { ...c.position, rowStart: c.position.rowStart + 1 },
      }))
      set({ containers: [container, ...otherContainersNewPosition] })
      return
    }
    if (rowStart > 1) {
      const newRowStart = rowStart - 1
      const newPosition = { ...position, rowStart: newRowStart }
      get().handleLayoutChange(containerId, newPosition)
    }
  },

  decrVerticalTop: (containerId) => {
    const containers = get().containers
    const container = containers.find((c) => c.id === containerId)
    if (!container) return
    const { position } = container
    const { rowStart, rowSpan } = position
    const newRowStart = rowStart + 1
    const newRowSpan = rowSpan - 1
    const newPosition = { ...position, rowStart: newRowStart, rowSpan: newRowSpan }
    if (rowStart === 1) {
      const nrOfContainersInFirstRow = containers.filter((c) => c.position.rowStart === 1).length
      if (nrOfContainersInFirstRow > 1) {
        get().handleLayoutChange(containerId, newPosition)
        return
      }
      if (nrOfContainersInFirstRow === 1) {
        const otherContainers = containers.filter((c) => c.id !== containerId)
        const otherContainersNewPosition = otherContainers.map((c) => ({
          ...c,
          position: { ...c.position, rowStart: c.position.rowStart - 1 },
        }))
        set({
          containers: [
            { ...container, position: { ...position, rowStart: 1, rowSpan: newRowSpan } },
            ...otherContainersNewPosition,
          ],
        })
        return
      }
    }
    get().handleLayoutChange(containerId, newPosition)
  },

  decrHorizontalRight: (containerId) => {
    const containers = get().containers
    const container = containers.find((c) => c.id === containerId)
    if (!container) return
    const { position } = container
    const { colSpan } = position
    if (colSpan > 1) {
      const newColSpan = colSpan - 1
      const newPosition = { ...position, colSpan: newColSpan }
      get().handleLayoutChange(containerId, newPosition)
    }
  },

  moveRight: (containerId) => {
    const containers = get().containers
    const container = containers.find((c) => c.id === containerId)
    if (!container) return
    const { position } = container
    const { colStart } = position
    const newColStart = colStart + 1
    const newPosition = { ...position, colStart: newColStart }
    if (colStart === 1) {
      const nrOfContainersInFirstColumn = containers.filter((c) => c.position.colStart === 1).length
      if (nrOfContainersInFirstColumn > 1) {
        get().handleLayoutChange(containerId, newPosition)
        return
      }
      if (nrOfContainersInFirstColumn === 1) {
        const otherContainers = containers.filter((c) => c.id !== containerId)
        const otherContainersNewPosition = otherContainers.map((c) => ({
          ...c,
          position: { ...c.position, colStart: c.position.colStart - 1 },
        }))
        set({ containers: [container, ...otherContainersNewPosition] })
        return
      }
    }
    get().handleLayoutChange(containerId, newPosition)
  },

  incrHorizontalRight: (containerId) => {
    const containers = get().containers
    const container = containers.find((c) => c.id === containerId)
    if (!container) return
    const { position } = container
    const { colSpan } = position
    const newColSpan = colSpan + 1
    const newPosition = { ...position, colSpan: newColSpan }
    get().handleLayoutChange(containerId, newPosition)
  },

  incrVerticalBottom: (containerId) => {
    const containers = get().containers
    const container = containers.find((c) => c.id === containerId)
    if (!container) return
    const { position } = container
    const { rowSpan } = position
    const newRowSpan = rowSpan + 1
    const newPosition = { ...position, rowSpan: newRowSpan }
    get().handleLayoutChange(containerId, newPosition)
  },

  moveDown: (containerId) => {
    const containers = get().containers
    const container = containers.find((c) => c.id === containerId)
    if (!container) return
    const { position } = container
    const { rowStart } = position
    const newRowStart = rowStart + 1
    const newPosition = { ...position, rowStart: newRowStart }
    if (rowStart === 1) {
      const nrOfContainersInFirstRow = containers.filter((c) => c.position.rowStart === 1).length
      if (nrOfContainersInFirstRow > 1) {
        get().handleLayoutChange(containerId, newPosition)
        return
      }
      if (nrOfContainersInFirstRow === 1) {
        const otherContainers = containers.filter((c) => c.id !== containerId)
        const otherContainersNewPosition = otherContainers.map((c) => ({
          ...c,
          position: { ...c.position, rowStart: c.position.rowStart - 1 },
        }))
        set({ containers: [container, ...otherContainersNewPosition] })
        return
      }
    }
    get().handleLayoutChange(containerId, newPosition)
  },

  decrVerticalBottom: (containerId) => {
    const containers = get().containers
    const container = containers.find((c) => c.id === containerId)
    if (!container) return
    const { position } = container
    const { rowSpan } = position
    if (rowSpan < 2) return
    const newRowSpan = rowSpan - 1
    const newPosition = { ...position, rowSpan: newRowSpan }
    get().handleLayoutChange(containerId, newPosition)
  },

  decrHorizontalLeft: (containerId) => {
    const containers = get().containers
    const container = containers.find((c) => c.id === containerId)
    if (!container) return
    const { position } = container
    const { colStart, colSpan } = position
    if (colSpan < 2) return
    const newColStart = colStart + 1
    const newColSpan = colSpan - 1
    const newPosition = { ...position, colStart: newColStart, colSpan: newColSpan }
    if (colStart === 1) {
      const nrOfContainersInFirstColumn = containers.filter((c) => c.position.colStart === 1).length
      if (nrOfContainersInFirstColumn > 1) {
        get().handleLayoutChange(containerId, newPosition)
        return
      }
      if (nrOfContainersInFirstColumn === 1) {
        const otherContainers = containers.filter((c) => c.id !== containerId)
        const otherContainersNewPosition = otherContainers.map((c) => ({
          ...c,
          position: { ...c.position, colStart: c.position.colStart - 1 },
        }))
        set({
          containers: [
            { ...container, position: { ...position, colStart: 1, colSpan: newColSpan } },
            ...otherContainersNewPosition,
          ],
        })
        return
      }
    }
    get().handleLayoutChange(containerId, newPosition)
  },

  moveLeft: (containerId) => {
    const containers = get().containers
    const container = containers.find((c) => c.id === containerId)
    if (!container) return
    const { position } = container
    const { colStart } = position
    if (colStart > 1) {
      const newColStart = colStart - 1
      const newPosition = { ...position, colStart: newColStart }
      get().handleLayoutChange(containerId, newPosition)
    }
    if (colStart === 1) {
      const otherContainers = containers.filter((c) => c.id !== containerId)
      const otherContainersNewPosition = otherContainers.map((c) => ({
        ...c,
        position: { ...c.position, colStart: c.position.colStart + 1 },
      }))
      set({ containers: [container, ...otherContainersNewPosition] })
    }
  },

  incrHorizontalLeft: (containerId) => {
    const containers = get().containers
    const container = containers.find((c) => c.id === containerId)
    if (!container) return
    const { position } = container
    const { colStart, colSpan } = position
    const newColStart = colStart - 1
    const newColSpan = colSpan + 1
    const newPosition = { ...position, colStart: newColStart, colSpan: newColSpan }
    if (colStart === 1) {
      const otherContainers = containers.filter((c) => c.id !== containerId)
      const otherContainersNewPosition = otherContainers.map((c) => ({
        ...c,
        position: { ...c.position, colStart: c.position.colStart + 1 },
      }))
      set({
        containers: [
          { ...container, position: { ...position, colStart: 1, colSpan: newColSpan } },
          ...otherContainersNewPosition,
        ],
      })
      return
    }
    get().handleLayoutChange(containerId, newPosition)
  },

  applyLayout: (layout) => {
    const { gridCols, gridRows } = get()
    const byId = new Map(layout.map((l) => [l.i, l]))

    // Helper to check free area
    const isAreaFree = (occ: boolean[][], r: number, c: number, h: number, w: number) => {
      if (r + h - 1 > gridRows || c + w - 1 > gridCols) return false
      for (let rr = r; rr < r + h; rr++) {
        for (let cc = c; cc < c + w; cc++) {
          if (occ[rr - 1][cc - 1]) return false
        }
      }
      return true
    }
    const markArea = (occ: boolean[][], r: number, c: number, h: number, w: number) => {
      for (let rr = r; rr < r + h; rr++) {
        for (let cc = c; cc < c + w; cc++) {
          occ[rr - 1][cc - 1] = true
        }
      }
    }

    const occupancy: boolean[][] = Array.from({ length: gridRows }, () => Array.from({ length: gridCols }, () => false))
    const placed: TContainer[] = []

    for (const c of get().containers) {
      const l = byId.get(c.id)
      const proposed = l
        ? {
            rowStart: Math.max(1, l.y + 1),
            colStart: Math.max(1, l.x + 1),
            rowSpan: Math.max(1, l.h),
            colSpan: Math.max(1, l.w),
          }
        : c.position

      // clamp spans
      let rowSpan = Math.min(proposed.rowSpan, gridRows)
      let colSpan = Math.min(proposed.colSpan, gridCols)
      // find target position: try proposed; else first free cell
      let targetR = Math.min(proposed.rowStart, Math.max(1, gridRows - rowSpan + 1))
      let targetC = Math.min(proposed.colStart, Math.max(1, gridCols - colSpan + 1))

      if (!isAreaFree(occupancy, targetR, targetC, rowSpan, colSpan)) {
        let found = false
        outer: for (let rr = 1; rr <= gridRows; rr++) {
          for (let cc = 1; cc <= gridCols; cc++) {
            if (isAreaFree(occupancy, rr, cc, rowSpan, colSpan)) {
              targetR = rr
              targetC = cc
              found = true
              break outer
            }
          }
        }
        if (!found) {
          // fall back: shrink to 1x1 if necessary
          rowSpan = 1
          colSpan = 1
          outer2: for (let rr = 1; rr <= gridRows; rr++) {
            for (let cc = 1; cc <= gridCols; cc++) {
              if (!occupancy[rr - 1][cc - 1]) {
                targetR = rr
                targetC = cc
                break outer2
              }
            }
          }
        }
      }

      markArea(occupancy, targetR, targetC, rowSpan, colSpan)
      placed.push({
        ...c,
        position: { rowStart: targetR, colStart: targetC, rowSpan, colSpan },
      })
    }

    set({ containers: placed })
  },

  // grid controls: Option A (block destructive shrink)
  addColumn: () => set((s) => ({ gridCols: s.gridCols + 1 })),
  addRow: () => set((s) => ({ gridRows: s.gridRows + 1 })),
  removeColumn: () => {
    const { gridCols, containers } = get()
    if (gridCols <= 1) return
    const nextCols = gridCols - 1
    const offending = containers.filter((c) => c.position.colStart + c.position.colSpan - 1 > nextCols)
    if (offending.length > 0) {
      toast.error(`Cannot remove column. ${offending.length} tile(s) would be clipped.`)
      return
    }
    set({ gridCols: nextCols })
  },
  removeRow: () => {
    const { gridRows, containers } = get()
    if (gridRows <= 1) return
    const nextRows = gridRows - 1
    const offending = containers.filter((c) => c.position.rowStart + c.position.rowSpan - 1 > nextRows)
    if (offending.length > 0) {
      toast.error(`Cannot remove row. ${offending.length} tile(s) would be clipped.`)
      return
    }
    set({ gridRows: nextRows })
  },
  setGridCols: (cols) => set({ gridCols: cols }),
  setGridRows: (rows) => set({ gridRows: rows }),
  trimGrid: () => {
    const { containers } = get()
    set({ gridCols: computeCols(containers), gridRows: computeRows(containers) })
  },
}))

interface PageLayoutProviderProps {
  children: ReactNode
}

export function PageLayoutProvider({ children }: PageLayoutProviderProps) {
  const pathname = useLocation().pathname
  const isPageBuilder = useIsPageBuilder()
  const { setIsConfiguringLayout } = useAppPageState()
  const { data } = useGetAppPage()

  const base = usePageLayoutStore((s) => s.baseUiSchema)
  const initializeFromData = usePageLayoutStore((s) => s.initializeFromData)
  const currentLayoutId = usePageLayoutStore((s) => s.currentLayoutId)

  // Initialize store from server data once
  const isAppPage = pathname.includes('/apps/')
  useEffect(() => {
    // if (!base || isAppPage) initializeFromData(data)
    if (isPageBuilder && !isAppPage) initializeFromData(DEFAULT_PAGE_LAYOUT)
    else initializeFromData(data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, data, isPageBuilder, isAppPage])

  // Keep configure-layout mode in sync with whether a saved layout is active
  useEffect(() => {
    if (isPageBuilder) {
      setIsConfiguringLayout(!currentLayoutId)
    }
  }, [isPageBuilder, currentLayoutId, setIsConfiguringLayout])

  return children
}

export function useAppPageLayout() {
  const containers = usePageLayoutStore((s) => s.containers)
  const titleDesrcription = usePageLayoutStore((s) => s.titleDesrcription)
  const currentLayoutId = usePageLayoutStore((s) => s.currentLayoutId)
  const gridCols = usePageLayoutStore((s) => s.gridCols)
  const gridRows = usePageLayoutStore((s) => s.gridRows)

  const rows = useMemo(() => computeRows(containers), [containers])
  const cols = useMemo(() => computeCols(containers), [containers])
  const numberOfContainers = containers.length

  const setTitleDesrcription = usePageLayoutStore((s) => s.setTitleDesrcription)
  const setDefaultContainers = usePageLayoutStore((s) => s.setDefaultContainers)
  const handleLayoutChange = usePageLayoutStore((s) => s.handleLayoutChange)
  const handleResetPageLayout = usePageLayoutStore((s) => s.handleResetPageLayout)
  const handleAddContainer = usePageLayoutStore((s) => s.handleAddContainer)
  const setComponentType = usePageLayoutStore((s) => s.setComponentType)
  const handleDeleteContainer = usePageLayoutStore((s) => s.handleDeleteContainer)
  const copyJsonLayout = usePageLayoutStore((s) => s.copyJsonLayout)
  const createNewPageLayout = usePageLayoutStore((s) => s.createNewPageLayout)
  const savePageLayout = usePageLayoutStore((s) => s.savePageLayout)
  const updateSavedPageLayout = usePageLayoutStore((s) => s.updateSavedPageLayout)
  const loadPageLayout = usePageLayoutStore((s) => s.loadPageLayout)
  const deletePageLayout = usePageLayoutStore((s) => s.deletePageLayout)
  const setCurrentLayoutId = usePageLayoutStore((s) => s.setCurrentLayoutId)
  const incrVerticalTop = usePageLayoutStore((s) => s.incrVerticalTop)
  const moveUp = usePageLayoutStore((s) => s.moveUp)
  const decrVerticalTop = usePageLayoutStore((s) => s.decrVerticalTop)
  const decrHorizontalRight = usePageLayoutStore((s) => s.decrHorizontalRight)
  const moveRight = usePageLayoutStore((s) => s.moveRight)
  const incrHorizontalRight = usePageLayoutStore((s) => s.incrHorizontalRight)
  const incrVerticalBottom = usePageLayoutStore((s) => s.incrVerticalBottom)
  const moveDown = usePageLayoutStore((s) => s.moveDown)
  const decrVerticalBottom = usePageLayoutStore((s) => s.decrVerticalBottom)
  const decrHorizontalLeft = usePageLayoutStore((s) => s.decrHorizontalLeft)
  const moveLeft = usePageLayoutStore((s) => s.moveLeft)
  const incrHorizontalLeft = usePageLayoutStore((s) => s.incrHorizontalLeft)
  const applyLayout = usePageLayoutStore((s) => s.applyLayout)
  const addColumn = usePageLayoutStore((s) => s.addColumn)
  const removeColumn = usePageLayoutStore((s) => s.removeColumn)
  const addRow = usePageLayoutStore((s) => s.addRow)
  const removeRow = usePageLayoutStore((s) => s.removeRow)
  const setGridCols = usePageLayoutStore((s) => s.setGridCols)
  const setGridRows = usePageLayoutStore((s) => s.setGridRows)
  const trimGrid = usePageLayoutStore((s) => s.trimGrid)

  return {
    containers,
    rows,
    cols,
    gridCols,
    gridRows,
    titleDesrcription,
    currentLayoutId,
    numberOfContainers,
    setTitleDesrcription,
    setDefaultContainers,
    handleLayoutChange,
    handleResetPageLayout,
    handleAddContainer,
    setComponentType,
    handleDeleteContainer,
    copyJsonLayout,
    createNewPageLayout,
    savePageLayout,
    updateSavedPageLayout,
    loadPageLayout,
    deletePageLayout,
    setCurrentLayoutId,
    incrVerticalTop,
    moveUp,
    decrVerticalTop,
    decrHorizontalRight,
    moveRight,
    incrHorizontalRight,
    incrVerticalBottom,
    moveDown,
    decrVerticalBottom,
    decrHorizontalLeft,
    moveLeft,
    incrHorizontalLeft,
    applyLayout,
    addColumn,
    removeColumn,
    addRow,
    removeRow,
    setGridCols,
    setGridRows,
    trimGrid,
  }
}
